import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { apiErrorMessage } from "../../lib/errors";
import type { MeResponse } from "../../lib/auth";

interface OutletCtx {
  me: MeResponse;
  refreshMe: () => Promise<void>;
}

interface VoiceOption {
  id: string;
  name: string;
  status: "pending" | "training" | "ready" | "failed";
  source_count: number;
}

type Mood = "default" | "excited" | "serious" | "playful" | "contemplative";

const MOOD_OPTIONS: { value: Mood; label: string; hint: string }[] = [
  { value: "default", label: "Default", hint: "Use voice as-is." },
  { value: "excited", label: "Excited", hint: "Energetic, punchy." },
  { value: "serious", label: "Serious", hint: "Measured, weighty." },
  { value: "playful", label: "Playful", hint: "Witty, warm." },
  { value: "contemplative", label: "Contemplative", hint: "Reflective, slower." },
];

export default function NewProjectPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { me, refreshMe } = useOutletContext<OutletCtx>();
  const [title, setTitle] = useState("");
  const [inputText, setInputText] = useState("");
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [voiceProfileId, setVoiceProfileId] = useState<string>("");
  const [mood, setMood] = useState<Mood>("default");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ voice_profiles: VoiceOption[] }>("/api/voice-profiles")
      .then((res) => setVoices(res.voice_profiles ?? []))
      .catch(() => setVoices([]));
  }, []);

  const charCount = inputText.length;
  const tooShort = charCount > 0 && charCount < 50;
  const tooLong = charCount > 20000;
  const noCredits = me.credits.remaining < 1;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (tooShort || tooLong || noCredits) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post<{ project_id: string }>("/api/projects", {
        title: title.trim() || undefined,
        input_text: inputText.trim(),
        voice_profile_id: voiceProfileId || undefined,
        mood: mood !== "default" ? mood : undefined,
      });
      await refreshMe();
      toast.success("Generation queued — variants will appear shortly.");
      navigate(`/app/projects/${res.project_id}`);
    } catch (err) {
      const msg = apiErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="page-title">New project</h1>
        <Link to="/app" className="text-sm text-ink-500 no-underline hover:text-ink-900">
          ← Back to projects
        </Link>
      </div>

      {noCredits ? (
        <div className="card mb-6 border-yellow-300 bg-yellow-50">
          <p className="text-yellow-900">
            You've used all <strong>{me.credits.quota}</strong> generations on the{" "}
            <strong>{me.credits.plan}</strong> plan this month.{" "}
            <Link to="/pricing" className="font-medium">
              Upgrade
            </Link>{" "}
            for more, or wait until{" "}
            {new Date(me.credits.resets_at).toLocaleDateString()} when your quota
            resets.
          </p>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="card space-y-5">
        <div>
          <label className="mb-1 block text-sm font-medium text-ink-700">
            Title <span className="text-ink-500">(optional)</span>
          </label>
          <input
            className="input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Why most teams measure the wrong metrics"
            maxLength={200}
          />
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="block text-sm font-medium text-ink-700">
              Input — paste your long-form content
            </label>
            <span
              className={`text-xs ${
                tooLong ? "text-red-600" : tooShort ? "text-yellow-600" : "text-ink-500"
              }`}
            >
              {charCount.toLocaleString()} / 20,000
            </span>
          </div>
          <textarea
            className="input min-h-[280px] font-mono text-sm leading-relaxed"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Paste a blog post, an email, a meeting note, or any long-form text. Minimum 50 characters."
          />
          {tooShort ? (
            <p className="mt-1 text-xs text-yellow-700">At least 50 characters please.</p>
          ) : null}
          {tooLong ? (
            <p className="mt-1 text-xs text-red-700">Too long — keep it under 20,000 characters.</p>
          ) : null}
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="block text-sm font-medium text-ink-700">
              Voice profile <span className="text-ink-500">(optional)</span>
            </label>
            <Link
              to="/app/voices/new"
              className="text-xs text-ink-500 no-underline hover:text-ink-900"
            >
              + Train new voice
            </Link>
          </div>
          {voices.length === 0 ? (
            <div className="rounded border border-dashed border-ink-200 p-3 text-xs text-ink-500">
              No voice profiles yet. Generation will use a neutral voice.{" "}
              <Link to="/app/voices/new" className="font-medium">
                Train one
              </Link>{" "}
              to make output sound like you.
            </div>
          ) : (
            <select
              value={voiceProfileId}
              onChange={(e) => setVoiceProfileId(e.target.value)}
              className="input"
            >
              <option value="">Neutral voice (no profile)</option>
              {voices.map((v) => (
                <option
                  key={v.id}
                  value={v.id}
                  disabled={v.status !== "ready"}
                >
                  {v.name}
                  {v.status !== "ready" ? ` — ${v.status}` : ""}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-ink-700">
            Mood <span className="text-ink-500">(optional)</span>
          </label>
          <p className="mb-2 text-xs text-ink-500">
            Nudge the tone of this generation without retraining the voice.
          </p>
          <div className="flex flex-wrap gap-2">
            {MOOD_OPTIONS.map((m) => {
              const active = mood === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMood(m.value)}
                  className={`flex flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors ${
                    active
                      ? "border-ink-900 bg-ink-900 text-white"
                      : "border-ink-200 bg-white text-ink-900 hover:border-ink-400"
                  }`}
                >
                  <span className="text-sm font-medium">{m.label}</span>
                  <span
                    className={`text-xs ${active ? "text-ink-200" : "text-ink-500"}`}
                  >
                    {m.hint}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-lg bg-ink-100 p-4 text-sm text-ink-700">
          <p>
            We'll generate <strong>5 native variants</strong>: X thread, Bluesky thread,
            LinkedIn post, email newsletter, and short-form video script. This costs{" "}
            <strong>1 generation</strong>.
            Remaining this month:{" "}
            <strong>
              {me.credits.remaining}/{me.credits.quota}
            </strong>{" "}
            ({me.credits.plan} plan).
          </p>
        </div>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <div className="flex gap-3">
          <button
            type="submit"
            className="btn-brand"
            disabled={submitting || tooShort || tooLong || noCredits || charCount === 0}
          >
            {submitting ? "Submitting…" : "Generate variants"}
          </button>
          <Link to="/app" className="btn-secondary no-underline">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
